import { Calendar, CheckCircle, Clock, Settings, Timer } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAssignTemplateToWorker,
  useCreateCustomWorkerPlan,
} from "@/domains/shifts/hooks/use-shift-mutations";
import type { CustomScheduleInput } from "@/gql/graphql";
import { CustomScheduleBuilder } from "./custom-schedule-builder";

const createDefaultSchedule = (): CustomScheduleInput => ({
  scheduleType: "weekly",
  days: [1, 2, 3, 4, 5], // Mon-Fri default
  timeIntervals: [
    {
      startTime: "09:00",
      endTime: "17:00",
    },
  ],
  breakRules: [],
});

interface CreateWorkerPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workerSalonId: string | null;
  templates: any[];
}

export function CreateWorkerPlanDialog({
  open,
  onOpenChange,
  workerSalonId,
  templates,
}: CreateWorkerPlanDialogProps) {
  const [selectedTab, setSelectedTab] = useState<"template" | "custom">("template");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [customSchedule, setCustomSchedule] = useState<CustomScheduleInput>(createDefaultSchedule);

  // The dialog stays mounted while workerSalonId changes, so state from the previous
  // worker survives a Cancel. The parent closes the dialog between workers, so reopening
  // is the point where every worker gets a clean form.
  useEffect(() => {
    if (!open) return;
    setSelectedTab("template");
    setSelectedTemplateId(null);
    setCustomSchedule(createDefaultSchedule());
  }, [open]);

  const { execute: assignTemplate, isLoading: assignLoading } = useAssignTemplateToWorker();
  const { execute: createCustomPlan, isLoading: createLoading } = useCreateCustomWorkerPlan();

  const handleAssignTemplate = async () => {
    if (!workerSalonId || !selectedTemplateId) return;

    try {
      await assignTemplate(selectedTemplateId, workerSalonId);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to assign template:", error);
    }
  };

  const handleCreateCustomPlan = async () => {
    if (!workerSalonId) return;

    try {
      await createCustomPlan(workerSalonId, customSchedule);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to create custom plan:", error);
    }
  };

  const getDayNames = (days: number[]) => {
    const dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map((d) => dayNames[d]).join(", ");
  };

  const getTimeRange = (intervals: any[]) => {
    if (intervals.length === 0) return "No time specified";
    if (intervals.length === 1) {
      return `${intervals[0].startTime} - ${intervals[0].endTime}`;
    }
    return `${intervals[0].startTime} - ${intervals[intervals.length - 1].endTime} (${intervals.length} intervals)`;
  };

  const _selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Worker Plan</DialogTitle>
          <DialogDescription>
            Choose how to schedule this worker: assign an existing template or create a custom
            schedule.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={selectedTab}
          onValueChange={(value) => setSelectedTab(value as "template" | "custom")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="template" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Template Mode
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Custom Mode
            </TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="space-y-4">
            <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-blue-900 dark:text-blue-100 text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Template Mode Benefits
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 text-sm text-blue-800 dark:text-blue-200">
                <ul className="space-y-1 text-xs">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Template changes automatically update this worker's schedule</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Consistent scheduling across multiple workers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Easy to manage and maintain</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {templates.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Clock className="w-8 h-8 text-muted-foreground mb-2" />
                  <h3 className="font-medium mb-1">No Templates Available</h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Create shift templates first to assign them to workers.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      // TODO: Navigate to templates tab
                    }}
                  >
                    Create Templates
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <h3 className="font-medium text-sm">Choose a Template</h3>
                <div className="grid gap-3 max-h-60 overflow-y-auto">
                  {templates.map((template) => (
                    <Card
                      key={template.id}
                      className={`cursor-pointer transition-colors ${
                        selectedTemplateId === template.id
                          ? "ring-2 ring-primary border-primary"
                          : "hover:border-primary/50"
                      }`}
                      onClick={() => setSelectedTemplateId(template.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: template.color || "#6b7280" }}
                            />
                            <h4 className="font-medium text-sm">{template.name}</h4>
                          </div>
                          {selectedTemplateId === template.id && (
                            <CheckCircle className="w-4 h-4 text-primary" />
                          )}
                        </div>

                        {template.description && (
                          <p className="text-xs text-muted-foreground mb-2">
                            {template.description}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{getDayNames(template.days)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Timer className="w-3 h-3" />
                            <span>{getTimeRange(template.timeIntervals || [])}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAssignTemplate}
                disabled={!selectedTemplateId || assignLoading}
              >
                {assignLoading ? "Assigning..." : "Assign Template"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-orange-900 dark:text-orange-100 text-sm flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Custom Mode Benefits
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 text-sm text-orange-800 dark:text-orange-200">
                <ul className="space-y-1 text-xs">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Completely independent of templates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Perfect for unique worker needs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Full control over schedule details</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h3 className="font-medium text-sm">Build Custom Schedule</h3>

              <CustomScheduleBuilder value={customSchedule} onChange={setCustomSchedule} />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateCustomPlan}
                disabled={
                  createLoading ||
                  !customSchedule.days.length ||
                  !customSchedule.timeIntervals?.length
                }
              >
                {createLoading ? "Creating..." : "Create Custom Plan"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
